// Data access for the active company. RLS enforces the same scoping server-side;
// the company filter here only keeps multi-company users' views separate.
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { AuditMember } from './auditAttribution';
import type { ImportMember } from './memberImport';
import type { ReportSourceMember } from './monthlyReport';
import { supabase } from './supabase';
import { collectById } from './presenceView';
import { companySetupCounts } from './companySetupCounts';
import { auditPageRange } from './auditPagination';
import { announcementPageRange } from './announcementPagination';
import { gdprPageRange } from './gdprPagination';
import type { AuditEntry, BatchDocument, Company, GdprRequest, Invitation, Kiosk, LeaveType, Member, PayrollBatch, PayrollBatchStats, Site } from './types';

export function useCompany() {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['company', companyId],
    enabled: Boolean(companyId),
    queryFn: async () => {
      const { data, error } = await supabase.from('companies').select('id, legal_name, vat_number, fiscal_code, pec, sdi_code, inps_matricola, ccnl, address, updated_at').eq('id', companyId).single();
      if (error) throw error;
      return data as Company;
    },
  });
}

export function useInvitations(memberId: string) {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['invitations', companyId, memberId],
    enabled: Boolean(companyId && memberId),
    refetchInterval: 60_000,
    queryFn: async () => {
      const all = await collectById<Invitation>((after) => {
        let query = supabase.from('invitations').select('id, member_id, email, status, created_at, expires_at, redeemed_at').eq('company_id', companyId).eq('member_id', memberId).order('id').limit(500);
        if (after) query = query.gt('id', after);
        return query.overrideTypes<Invitation[], { merge: false }>();
      });
      return all.sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id.localeCompare(a.id));
    },
  });
}

function useCompanyId(): string {
  const { active } = useAuth();
  return active?.company_id ?? '';
}

/** Setup guide only: aggregate counts, never a directory/key download. */
export function useCompanySetupCounts() {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['members', companyId, 'setup-counts'], enabled: Boolean(companyId),
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('company_setup_counts', { p_company_id: companyId });
      if (error) throw error;
      return companySetupCounts(data);
    },
  });
}

export interface CompanyAnnouncement {
  id: string;
  title: string;
  body: string;
  category?: 'notice' | 'circular' | 'regulation' | 'privacy_notice' | 'disciplinary_code';
  requires_ack?: boolean;
  published_at: string;
  expires_at: string | null;
  archived_at: string | null;
}
export function useAnnouncements(companyId: string, page = 0) {
  return useQuery({
    queryKey: ['announcements', companyId, page], enabled: Boolean(companyId),
    queryFn: async () => {
      const { from, to } = announcementPageRange(page);
      const { data, error, count } = await supabase.from('company_announcements')
        .select('id,title,body,category,requires_ack,published_at,expires_at,archived_at', { count: 'exact' })
        .eq('company_id', companyId).is('archived_at', null)
        .order('published_at', { ascending: false }).order('id', { ascending: false })
        .range(from, to);
      if (error) throw error;
      if (count === null) throw new Error('count_unavailable');
      return { rows: (data ?? []) as CompanyAnnouncement[], total: count };
    },
  });
}

async function rows<T>(promise: PromiseLike<{ data: unknown; error: unknown }>): Promise<T[]> {
  const { data, error } = await promise;
  if (error) throw error;
  return (data ?? []) as T[];
}

const MEMBER_COLUMNS = 'id, company_id, auth_user_id, role, status, full_name, site_id, manager_member_id, employee_number, preferred_language, terminated_on, status_changed_at, member_identities(email, codice_fiscale), device_keys(id, status, fingerprint, x25519_public_key, ed25519_public_key, created_at)';

/** Selected employee only; changing company cannot reuse another company's detail. */
export function useMember(memberId: string) {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['members', companyId, 'detail', memberId], enabled: Boolean(companyId && memberId),
    queryFn: async () => {
      const { data, error } = await supabase.from('members').select(MEMBER_COLUMNS).eq('company_id', companyId).eq('id', memberId).single().overrideTypes<Member, { merge: false }>();
      if (error) throw error;
      return data;
    },
  });
}

export function useMembers() {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['members', companyId],
    enabled: Boolean(companyId),
    queryFn: async () => {
      const members = await collectById<Member>((after) => {
        let query = supabase
          .from('members')
          .select(MEMBER_COLUMNS)
          .eq('company_id', companyId)
          .order('id').limit(500);
        if (after) query = query.gt('id', after);
        return query.overrideTypes<Member[], { merge: false }>();
      });
      return members.sort((a, b) => a.full_name.localeCompare(b.full_name));
    },
  });
}

/** Monthly report needs employment state and fiscal code, never device keys or e-mail. */
export function useReportMembers() {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['members', companyId, 'report'], enabled: Boolean(companyId),
    queryFn: () => collectById<ReportSourceMember>((after) => {
      let query = supabase.from('members')
        .select('id, full_name, status, employee_number, member_identities(codice_fiscale)')
        .eq('company_id', companyId).order('id').limit(500);
      if (after) query = query.gt('id', after);
      return query.overrideTypes<ReportSourceMember[], { merge: false }>();
    }),
  });
}

/** Duplicate review needs identity matching, never device keys or employment details. */
export function useImportMembers() {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['members', companyId, 'import'], enabled: Boolean(companyId),
    queryFn: () => collectById<ImportMember>((after) => {
      let query = supabase.from('members')
        .select('id, full_name, member_identities(email, codice_fiscale)')
        .eq('company_id', companyId).order('id').limit(500);
      if (after) query = query.gt('id', after);
      return query.overrideTypes<ImportMember[], { merge: false }>();
    }),
  });
}

/** Filter options only: no identity documents or device keys. */
export function useWorklistMembers(enabled = true) {
  const companyId = useCompanyId();
  type Person = Pick<Member, 'id' | 'company_id' | 'full_name' | 'employee_number' | 'site_id' | 'manager_member_id' | 'role' | 'status'>;
  return useQuery({
    queryKey: ['members', companyId, 'worklist'], enabled: enabled && Boolean(companyId),
    queryFn: () => collectById<Person>((after) => {
      let query = supabase.from('members').select('id, company_id, full_name, employee_number, site_id, manager_member_id, role, status').eq('company_id', companyId).order('id').limit(500);
      if (after) query = query.gt('id', after);
      return query.overrideTypes<Person[], { merge: false }>();
    }),
  });
}

/** Minimal same-company identity data used only to label audit events. */
export function useAuditAttributionMembers() {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['audit-attribution-members', companyId],
    enabled: Boolean(companyId),
    queryFn: () => collectById<AuditMember>((after) => {
      let query = supabase.from('members')
        .select('id, company_id, auth_user_id, full_name, device_keys(id)')
        .eq('company_id', companyId).order('id').limit(500);
      if (after) query = query.gt('id', after);
      return query.overrideTypes<AuditMember[], { merge: false }>();
    }),
  });
}

export function useSites() {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['sites', companyId],
    enabled: Boolean(companyId),
    queryFn: async () => {
      const sites = await collectById<Site>((after) => {
        let query = supabase.from('sites').select('*').eq('company_id', companyId).order('id').limit(500);
        if (after) query = query.gt('id', after);
        return query.overrideTypes<Site[], { merge: false }>();
      });
      return sites.sort((a, b) => a.name.localeCompare(b.name));
    },
  });
}

export function useKiosks() {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['kiosks', companyId],
    enabled: Boolean(companyId),
    queryFn: () =>
      rows<Kiosk>(supabase.from('kiosk_devices').select('id, site_id, name, status, paired_at').eq('company_id', companyId).order('created_at')),
  });
}

export function useAuditLog(page: number) {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['audit', companyId, page],
    enabled: Boolean(companyId),
    queryFn: async () => {
      const { from, to } = auditPageRange(page);
      const { data, error, count } = await supabase
        .from('audit_log_hr')
        .select('id, table_name, row_id, operation, changed_columns, actor_auth_user_id, created_at', { count: 'exact' })
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to);
      if (error) throw error;
      return { rows: (data ?? []) as AuditEntry[], total: count ?? 0 };
    },
  });
}

export const PAYROLL_HISTORY_PAGE_SIZE = 10;
export const PAYROLL_DOCUMENT_PAGE_SIZE = 50;

/** Exact batch count; every batch remains reachable as history grows. */
export function usePayrollBatches(page: number) {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['payroll', companyId, 'batches', page],
    enabled: Boolean(companyId),
    queryFn: async () => {
      const from = page * PAYROLL_HISTORY_PAGE_SIZE;
      const { data, error, count } = await supabase.from('payroll_batches')
        .select('id, kind, period, title, status, created_at, published_at', { count: 'exact' })
        .eq('company_id', companyId)
        .order('created_at', { ascending: false }).order('id', { ascending: false })
        .range(from, from + PAYROLL_HISTORY_PAGE_SIZE - 1);
      if (error) throw error;
      if (count === null) throw new Error('count_unavailable');
      return { rows: (data ?? []) as PayrollBatch[], total: count };
    },
  });
}

/** The database aggregates all documents; the API row cap cannot truncate counts. */
export function usePayrollBatchStats(batchIds: string[]) {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['payroll', companyId, 'stats', batchIds],
    enabled: Boolean(companyId) && batchIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('payroll_batch_stats', { p_batch_ids: batchIds });
      if (error) throw error;
      const stats = (data ?? []) as PayrollBatchStats[];
      if (stats.length !== batchIds.length) throw new Error('payroll_stats_incomplete');
      return stats;
    },
  });
}

/** A document's recipient and withdrawal controls are fetched only on expansion. */
export function usePayrollBatchDocuments(batchId: string | null, page: number) {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['payroll', companyId, 'documents', batchId, page],
    enabled: Boolean(companyId) && Boolean(batchId),
    queryFn: async () => {
      const from = page * PAYROLL_DOCUMENT_PAGE_SIZE;
      const { data, error, count } = await supabase.from('documents')
        .select('id, batch_id, member_id, title, status, first_opened_at, storage_path', { count: 'exact' })
        .eq('company_id', companyId).eq('batch_id', batchId!)
        .order('id').range(from, from + PAYROLL_DOCUMENT_PAGE_SIZE - 1);
      if (error) throw error;
      if (count === null) throw new Error('count_unavailable');
      return { rows: (data ?? []) as BatchDocument[], total: count };
    },
  });
}

export function useLeaveTypes() {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['leaveTypes', companyId],
    enabled: Boolean(companyId),
    queryFn: () =>
      rows<LeaveType>(
        supabase
          .from('leave_types')
          .select('id, code, name, unit, requires_protocol, active, tracks_balance')
          .eq('company_id', companyId)
          .eq('active', true)
          .order('code'),
      ),
  });
}

/** The sidebar must show the exact open count and the earliest legal deadline. */
export function useGdprSummary(enabled = true) {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['gdpr', companyId, 'summary'], enabled: enabled && Boolean(companyId),
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error, count } = await supabase.from('gdpr_requests')
        .select('due_at', { count: 'exact' }).eq('company_id', companyId)
        .in('status', ['pending', 'in_progress']).order('due_at').limit(1);
      if (error) throw error;
      if (count === null) throw new Error('count_unavailable');
      return { total: count, earliestDue: data?.[0]?.due_at ?? null };
    },
  });
}

/** Open and closed GDPR requests use independent, complete server-side pages. */
export function useGdprRequestsPage(list: 'open' | 'closed', page: number) {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['gdpr', companyId, list, page], enabled: Boolean(companyId),
    queryFn: async () => {
      const { from, to } = gdprPageRange(page);
      let query = supabase.from('gdpr_requests')
        .select('id, member_id, kind, details, status, created_at, due_at, extended_at, extension_note, resolution_note, resolved_at', { count: 'exact' })
        .eq('company_id', companyId)
        .in('status', list === 'open' ? ['pending', 'in_progress'] : ['completed', 'rejected']);
      query = list === 'open'
        ? query.order('due_at').order('id')
        : query.order('resolved_at', { ascending: false, nullsFirst: false }).order('id', { ascending: false });
      const { data, error, count } = await query.range(from, to);
      if (error) throw error;
      if (count === null) throw new Error('count_unavailable');
      return { rows: (data ?? []) as GdprRequest[], total: count };
    },
  });
}

export function useBreakPolicies() {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['break_policies', companyId],
    enabled: Boolean(companyId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('company_break_policies')
        .select('id, company_id, name, is_paid, is_mandatory, min_duration_minutes, expected_duration_minutes, valid_from, valid_to')
        .eq('company_id', companyId)
        .order('valid_from', { ascending: false });
      if (error) throw error;
      return (data ?? []) as import('@fide/shared').BreakPolicy[];
    },
  });
}

export function useMemberPunches(memberId: string, month: string) {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['member_punches', companyId, memberId, month],
    enabled: Boolean(companyId && memberId && month),
    queryFn: async () => {
      const [y, m] = month.split('-').map(Number);
      const from = new Date(Date.UTC(y!, m! - 1, 1) - 24 * 3600_000).toISOString();
      const to = new Date(Date.UTC(y!, m!, 1) + 24 * 3600_000).toISOString();
      const { data, error } = await supabase
        .from('punches')
        .select('id, member_id, punch_type, method, device_ts, flags, break_policy_id')
        .eq('company_id', companyId)
        .eq('member_id', memberId)
        .gte('device_ts', from)
        .lte('device_ts', to)
        .order('device_ts');
      if (error) throw error;
      return (data ?? []) as import('@fide/shared').TimesheetPunch[];
    },
  });
}

export function useMemberSchedules(memberId: string) {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['member_schedules', companyId, memberId],
    enabled: Boolean(companyId && memberId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('work_schedules')
        .select('id, employment_terms_id, effective_from, slots, weekly_minutes, voided_at, site_id')
        .eq('company_id', companyId)
        .eq('member_id', memberId)
        .is('voided_at', null)
        .order('effective_from', { ascending: false });
      if (error) throw error;
      return (data ?? []) as import('@fide/shared').WorkSchedule[];
    },
  });
}

export function useMemberMonthLeaves(memberId: string, month: string) {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['member_month_leaves', companyId, memberId, month],
    enabled: Boolean(companyId && memberId && month),
    queryFn: async () => {
      const [y, m] = month.split('-').map(Number);
      const lastDay = new Date(Date.UTC(y!, m!, 0)).getUTCDate();
      const first = `${month}-01`;
      const last = `${month}-${String(lastDay).padStart(2, '0')}`;
      const { data, error } = await supabase
        .from('leave_requests')
        .select('id, member_id, start_date, end_date, quantity, status, daily_allocation, leave_types(code, unit)')
        .eq('company_id', companyId)
        .eq('member_id', memberId)
        .eq('status', 'approved')
        .lte('start_date', last)
        .gte('end_date', first);
      if (error) throw error;
      return (data ?? []) as unknown as import('@fide/shared').TimesheetLeave[];
    },
  });
}

export function usePendingOvertimeRequests() {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['overtime_requests', companyId, 'pending'],
    enabled: Boolean(companyId),
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('overtime_requests')
        .select('id, company_id, member_id, date, start_time, end_time, hours, compensation_type, reason, status, created_at, members!overtime_requests_company_id_member_id_fkey(full_name)')
        .eq('company_id', companyId)
        .eq('status', 'pending')
        .order('date', { ascending: false });
      if (error) throw error;
      return (data ?? []).map((row) => ({
        ...row,
        member_name: (Array.isArray(row.members) ? row.members[0]?.full_name : (row.members as {full_name?:string}|null)?.full_name) ?? null,
      })) as import('./types').OvertimeRequest[];
    },
  });
}

export function useMemberTimeContext(memberId:string, enabled:boolean) {
 const companyId=useCompanyId();
 return useQuery({queryKey:['member_time_context',companyId,memberId,enabled],enabled:Boolean(companyId&&memberId),queryFn:async()=>{
  if(!enabled)return {terms:[],calendar:[]};
  const [terms,calendar]=await Promise.all([
   supabase.from('employment_terms').select('id,effective_from,job_title,category,level,contract_type,ccnl_reference,weekly_hours,voided_at').eq('company_id',companyId).eq('member_id',memberId),
   supabase.rpc('member_calendar_days',{p_member_id:memberId}),
  ]);
  if(terms.error)throw terms.error;if(calendar.error)throw calendar.error;
  return {terms:terms.data as import('@fide/shared').EmploymentTerms[],calendar:calendar.data as import('@fide/shared').CalendarEntry[]};
 }});
}

export function useMonthClosed(monthIso: string) {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['monthClose', companyId, monthIso],
    enabled: Boolean(companyId && monthIso),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('month_close_events')
        .select('action')
        .eq('company_id', companyId)
        .eq('month', `${monthIso}-01`)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .limit(1);
      if (error) throw error;
      return (data?.[0] as { action?: string } | undefined)?.action === 'close';
    },
  });
}

export function useMealVoucherPolicy() {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['mealVoucherPolicy', companyId],
    enabled: Boolean(companyId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('company_meal_voucher_policies')
        .select('company_id, enabled, min_worked_minutes, include_smart_working, include_paid_breaks')
        .eq('company_id', companyId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as import('@fide/shared').MealVoucherPolicy | null;
    },
  });
}

export interface PagheMappingRow {
  fide_code: string;
  paghe_code: string;
}

export function usePagheMapping() {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['pagheMapping', companyId],
    enabled: Boolean(companyId),
    queryFn: () =>
      rows<PagheMappingRow>(
        supabase
          .from('company_paghe_mapping')
          .select('fide_code, paghe_code')
          .eq('company_id', companyId)
          .order('fide_code'),
      ),
  });
}

export interface AnnouncementAckRow {
  announcement_id: string;
  member_id: string;
  acknowledged_at: string;
}

export function useAnnouncementAcknowledgements(companyId: string, announcementIds: string[]) {
  return useQuery({
    queryKey: ['announcementAcks', companyId, announcementIds],
    enabled: Boolean(companyId) && announcementIds.length > 0,
    queryFn: () =>
      rows<AnnouncementAckRow>(
        supabase
          .from('company_announcement_acknowledgements')
          .select('announcement_id, member_id, acknowledged_at')
          .eq('company_id', companyId)
          .in('announcement_id', announcementIds)
          .order('acknowledged_at', { ascending: false }),
      ),
  });
}

export interface HrServiceRequestRow {
  id: string;
  company_id: string;
  member_id: string;
  category: 'iban_change' | 'address_change' | 'family_dependents' | 'service_certificate' | 'tfr_advance' | 'other';
  details: string;
  status: 'pending' | 'completed' | 'rejected' | 'cancelled';
  resolution_note: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  created_at: string;
}

export function useHrServiceRequests() {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: ['hrServiceRequests', companyId],
    enabled: Boolean(companyId),
    queryFn: () =>
      rows<HrServiceRequestRow>(
        supabase
          .from('hr_service_requests')
          .select('id, company_id, member_id, category, details, status, resolution_note, resolved_by, resolved_at, created_at')
          .eq('company_id', companyId)
          .order('created_at', { ascending: false })
          .limit(200),
      ),
  });
}
